/**
 * TargetPriceSummary — the saved target-price block on the position detail
 * (issue 3, 2026-10-17: "I cannot see the target price on the position
 * thesis"). Renders the latest saved scenario set (weighted target, scenario
 * prices when computed, per-scenario rationale, updated date) next to the
 * thesis summary. Edit reopens the builder seeded from the saved values —
 * the same TargetPriceCommand seed path the sheet's Save wrote from.
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigation } from '@react-navigation/native';
import {
  getPositionTargetPrice,
  type SavedTargetPrice,
} from '../../services/api/positionTargetPriceApi';

const SCENARIO_LABELS: { key: 'bull' | 'base' | 'bear'; labelKey: string; defaultLabel: string }[] = [
  { key: 'bull', labelKey: 'chat.targetPrice.bull', defaultLabel: 'Bull' },
  { key: 'base', labelKey: 'chat.targetPrice.base', defaultLabel: 'Base' },
  { key: 'bear', labelKey: 'chat.targetPrice.bear', defaultLabel: 'Bear' },
];

function fmt(v: number | null | undefined, currency: string | null): string {
  if (v == null) return '—';
  const rounded = Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : v.toFixed(2);
  return currency ? `${rounded} ${currency}` : rounded;
}

export function TargetPriceSummary({ positionId, symbol }: { positionId: number; symbol: string }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigation = useNavigation<any>();
  const styles = React.useMemo(() => createThemedStyles(theme), [theme]);

  const { data: tp, isLoading, isError } = useQuery<SavedTargetPrice | null>({
    queryKey: ['positionTargetPrice', positionId],
    queryFn: () => getPositionTargetPrice(positionId),
    staleTime: 60_000,
  });

  if (isLoading) return null;
  // No saved target price is a normal state, not an error banner — the
  // block simply renders as an invitation.
  if (isError || !tp) {
    return (
      <View style={styles.card}>
        <Text style={[styles.title, { color: theme.colors.text }]}>
          🎯 {t('portfolio.targetPriceTitle', { defaultValue: '目標價 Target Price' })}
        </Text>
        <Text style={[styles.empty, { color: theme.colors.textSecondary }]}>
          {t('portfolio.targetPriceNone', {
            defaultValue: '尚無儲存的目標價——在聊天中開啟目標價建立器即可開始。',
          })}
        </Text>
        <BuildButton
          styles={styles}
          theme={theme}
          t={t}
          onPress={() =>
            navigation.navigate('Chat', {
              screen: 'ChatScreen',
              params: {
                initialCommand: { command: 'target_price', symbol, target: { kind: 'position', id: positionId }, seed_snapshot: null },
              },
            })
          }
        />
      </View>
    );
  }

  const seed = {
    symbol: tp.symbol,
    bull: tp.scenarios.bull,
    base: tp.scenarios.base,
    bear: tp.scenarios.bear,
    bull_prob: tp.probabilities?.bull ?? null,
    base_prob: tp.probabilities?.base ?? null,
    bear_prob: tp.probabilities?.bear ?? null,
    currency: tp.currency,
    rationale: tp.rationale ?? null,
  };

  return (
    <View style={styles.card}>
      <Text style={[styles.title, { color: theme.colors.text }]}>
        🎯 {t('portfolio.targetPriceTitle', { defaultValue: '目標價 Target Price' })} — {tp.symbol}
      </Text>

      <Text style={[styles.weighted, { color: theme.colors.primary }]}>
        {t('portfolio.targetPriceWeighted', { defaultValue: '機率加權' })}:{' '}
        {fmt(tp.probability_weighted_target, tp.currency)}
      </Text>
      {tp.updated_at && (
        <Text style={[styles.updated, { color: theme.colors.textTertiary }]}>
          {t('portfolio.targetPriceUpdated', { defaultValue: '更新於' })} {tp.updated_at.slice(0, 10)}
        </Text>
      )}

      {SCENARIO_LABELS.map(({ key, labelKey, defaultLabel }) => {
        const price = tp.computed?.prices?.[key];
        const why = tp.rationale?.[key];
        return (
          <View key={key} style={styles.scenarioRow}>
            <Text style={[styles.scenarioLabel, { color: theme.colors.text }]}>
              {t(labelKey, { defaultValue: defaultLabel })}
              {tp.probabilities?.[key] != null
                ? ` (${Math.round((tp.probabilities[key] ?? 0) * 100)}%)`
                : ''}
            </Text>
            <Text style={[styles.scenarioPrice, { color: theme.colors.text }]}>
              {price != null ? fmt(price, tp.currency) : '—'}
            </Text>
            {why ? (
              <Text style={[styles.scenarioWhy, { color: theme.colors.textSecondary }]}>{why}</Text>
            ) : null}
          </View>
        );
      })}

      <BuildButton
        styles={styles}
        theme={theme}
        t={t}
        onPress={() =>
          navigation.navigate('Chat', {
            screen: 'ChatScreen',
            params: {
              initialCommand: {
                command: 'target_price',
                symbol: tp.symbol,
                target: { kind: 'position', id: positionId },
                seed_snapshot: seed,
              },
            },
          })
        }
      />
    </View>
  );
}

function BuildButton({
  styles,
  theme,
  t,
  onPress,
}: {
  styles: ReturnType<typeof createThemedStyles>;
  theme: any;
  t: any;
  onPress: () => void;
}) {
  return (
    <TouchableOpacity
      testID="tp-summary-build"
      style={[styles.buildButton, { borderColor: theme.colors.primary }]}
      onPress={onPress}
      accessibilityRole="button"
    >
      <Text style={{ color: theme.colors.primary, fontSize: theme.typography.fontSizeSm }}>
        {t('portfolio.targetPriceBuild', { defaultValue: '📐 建立 / 編輯目標價' })}
      </Text>
    </TouchableOpacity>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
      marginHorizontal: theme.spacing.md,
      marginTop: theme.spacing.sm,
      gap: theme.spacing.xs,
    },
    title: { fontSize: theme.typography.fontSizeMd, fontWeight: '600' },
    weighted: { fontSize: theme.typography.fontSizeLg, fontWeight: '700' },
    updated: { fontSize: theme.typography.fontSizeXs },
    empty: { fontSize: theme.typography.fontSizeSm },
    scenarioRow: { marginTop: theme.spacing.xs },
    scenarioLabel: { fontSize: theme.typography.fontSizeSm, fontWeight: '600' },
    scenarioPrice: { fontSize: theme.typography.fontSizeMd },
    scenarioWhy: {
      fontSize: theme.typography.fontSizeXs,
      lineHeight: theme.typography.fontSizeXs * 1.5,
      marginTop: 2,
    },
    buildButton: {
      borderWidth: 1,
      borderRadius: theme.radii.sm,
      paddingVertical: theme.spacing.sm,
      alignItems: 'center',
      marginTop: theme.spacing.xs,
    },
  });
}
